import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router } from '@inertiajs/react'
import { format } from 'date-fns'
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  SearchIcon,
  UserIcon,
  XIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface Patient {
  id: number
  display_name: string
  phone_number: string
  site_id: number
  has_care_case: boolean
}

interface Appointment {
  id: number
  patient: {
    id: number
    display_name: string
    phone_number: string
  }
  care_case_id: number
  site: {
    id: number
    name: string
  }
  appointment_type: string
  scheduled_at: string
  duration_minutes: number
  location: string | null
  reason: string | null
  notes: string | null
  status: string
}

interface AppointmentDialogProps {
  open: boolean
  onClose: () => void
  appointment?: Appointment | null
  patients?: Patient[]
  mode: 'create' | 'edit'
}

const appointmentSchema = z.object({
  patient_id: z.number({ required_error: 'Patient is required' }),
  appointment_type: z.string().min(1, 'Appointment type is required'),
  scheduled_date: z.date({ required_error: 'Date is required' }),
  scheduled_time: z.string().min(1, 'Time is required'),
  duration_minutes: z.number().min(5, 'Duration must be at least 5 minutes'),
  location: z.string().optional(),
  reason: z.string().optional(),
  notes: z.string().optional(),
})

type AppointmentFormData = z.infer<typeof appointmentSchema>

export function AppointmentDialog({
  open,
  onClose,
  appointment,
  patients = [],
  mode,
}: AppointmentDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showPatientSearch, setShowPatientSearch] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      duration_minutes: 30,
    },
  })

  const selectedPatientId = watch('patient_id')
  const selectedDate = watch('scheduled_date')

  // Load appointment data when editing
  useEffect(() => {
    if (mode === 'edit' && appointment) {
      const scheduledAt = new Date(appointment.scheduled_at)
      setValue('patient_id', appointment.patient.id)
      setValue('appointment_type', appointment.appointment_type)
      // Use UTC components — times are stored as wall-clock UTC (no offset conversion)
      setValue('scheduled_date', new Date(scheduledAt.getUTCFullYear(), scheduledAt.getUTCMonth(), scheduledAt.getUTCDate()))
      setValue('scheduled_time', `${String(scheduledAt.getUTCHours()).padStart(2, '0')}:${String(scheduledAt.getUTCMinutes()).padStart(2, '0')}`)
      setValue('duration_minutes', appointment.duration_minutes)
      setValue('location', appointment.location || '')
      setValue('reason', appointment.reason || '')
      setValue('notes', appointment.notes || '')
    } else if (mode === 'create') {
      reset({
        duration_minutes: 30,
      })
    }
  }, [mode, appointment, setValue, reset])

  const selectedPatient = patients.find(p => p.id === selectedPatientId)

  const filteredPatients = patients.filter(p =>
    p.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.phone_number.includes(searchQuery)
  )

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')

    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    }
  }

  const onSubmit = async (data: AppointmentFormData) => {
    setIsSubmitting(true)

    try {
      // Combine date and time — format without timezone offset so the backend
      // stores the wall-clock time as-is (no UTC conversion)
      const [hours, minutes] = data.scheduled_time.split(':')
      const d = data.scheduled_date
      const scheduledAtStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(parseInt(hours)).padStart(2, '0')}:${String(parseInt(minutes)).padStart(2, '0')}:00`

      const payload = {
        patient_id: data.patient_id,
        appointment_type: data.appointment_type,
        scheduled_at: scheduledAtStr,
        duration_minutes: data.duration_minutes,
        location: data.location || null,
        reason: data.reason || null,
        notes: data.notes || null,
      }

      if (mode === 'create') {
        await fetch('/admin/appointments', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }).then(async response => {
          const result = await response.json()
          if (response.ok) {
            if (result.message) {
              toast.success(result.message)
            }
            toast.success('Appointment created successfully')
            onClose()
            router.reload({ only: ['appointments', 'pagination'] })
          } else {
            toast.error(result.errors?.join(', ') || 'Failed to create appointment')
          }
        })
      } else if (mode === 'edit' && appointment) {
        await fetch(`/admin/appointments/${appointment.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }).then(async response => {
          const result = await response.json()
          if (response.ok) {
            toast.success('Appointment updated successfully')
            onClose()
            router.reload()
          } else {
            toast.error(result.errors?.join(', ') || 'Failed to update appointment')
          }
        })
      }
    } catch (error) {
      console.error('Error saving appointment:', error)
      toast.error('An error occurred while saving the appointment')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Appointment' : 'Edit Appointment'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Schedule a new appointment for a patient'
              : 'Update appointment details'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Patient Selection */}
          {mode === 'create' && (
            <div className="space-y-2">
              <Label htmlFor="patient">Patient *</Label>
              {selectedPatient ? (
                <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted">
                  <UserIcon className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{selectedPatient.display_name}</div>
                    <div className="text-sm text-muted-foreground">{selectedPatient.phone_number}</div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setValue('patient_id', 0)
                      setSearchQuery('')
                    }}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or phone number"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value)
                        setShowPatientSearch(true)
                      }}
                      onFocus={() => setShowPatientSearch(true)}
                      className="pl-10"
                    />
                  </div>
                  {showPatientSearch && searchQuery && (
                    <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                      {filteredPatients.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground text-center">
                          No patients found
                        </div>
                      ) : (
                        filteredPatients.map(patient => (
                          <button
                            key={patient.id}
                            type="button"
                            onClick={() => {
                              setValue('patient_id', patient.id)
                              setShowPatientSearch(false)
                              setSearchQuery('')
                            }}
                            className="w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors text-left"
                          >
                            <UserIcon className="h-4 w-4 text-muted-foreground" />
                            <div className="flex-1">
                              <div className="font-medium">{patient.display_name}</div>
                              <div className="text-sm text-muted-foreground">{patient.phone_number}</div>
                            </div>
                            {!patient.has_care_case && (
                              <Badge variant="secondary" className="text-xs">
                                New
                              </Badge>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
              {errors.patient_id && (
                <p className="text-sm text-destructive">{errors.patient_id.message}</p>
              )}
            </div>
          )}

          {/* Appointment Type */}
          <div className="space-y-2">
            <Label htmlFor="appointment_type">Appointment Type *</Label>
            <Select
              value={watch('appointment_type')}
              onValueChange={(value) => setValue('appointment_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select appointment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="initial_consultation">Initial Consultation</SelectItem>
                <SelectItem value="follow_up_visit">Follow-up Visit</SelectItem>
                <SelectItem value="lab_review">Lab Review</SelectItem>
                <SelectItem value="check_in">Check-in</SelectItem>
                <SelectItem value="phone_call">Phone Call</SelectItem>
                <SelectItem value="telehealth">Telehealth</SelectItem>
              </SelectContent>
            </Select>
            {errors.appointment_type && (
              <p className="text-sm text-destructive">{errors.appointment_type.message}</p>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !selectedDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setValue('scheduled_date', date)}
                  />
                </PopoverContent>
              </Popover>
              {errors.scheduled_date && (
                <p className="text-sm text-destructive">{errors.scheduled_date.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="scheduled_time">Time *</Label>
              <div className="relative">
                <ClockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="scheduled_time"
                  type="time"
                  {...register('scheduled_time')}
                  className="pl-10"
                />
              </div>
              {errors.scheduled_time && (
                <p className="text-sm text-destructive">{errors.scheduled_time.message}</p>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="duration_minutes">Duration (minutes) *</Label>
            <Input
              id="duration_minutes"
              type="number"
              {...register('duration_minutes', { valueAsNumber: true })}
              min={5}
              step={5}
            />
            {errors.duration_minutes && (
              <p className="text-sm text-destructive">{errors.duration_minutes.message}</p>
            )}
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="location"
                {...register('location')}
                placeholder="e.g., Room 101 or https://zoom.us/..."
                className="pl-10"
              />
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Visit</Label>
            <Textarea
              id="reason"
              {...register('reason')}
              placeholder="Brief description of the appointment purpose"
              rows={2}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Clinical Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any additional notes or instructions"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Appointment' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
